import clsx from 'clsx';
import { useStore } from '../../../../hooks/useStore';
import Menu from '../Menu';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import {
  IconButton,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  XIcon
} from 'stubben-edge-unified-frontend-react-components';
import { SidebarCollapsedProvider } from './SidebarCollapsedContext';
import SidebarFooter from './SidebarFooter';

export const SIDEBAR_WIDTH_TRANSITION_MS = 300;

type SidebarProps = {
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
};

const Sidebar = ({ collapsed = true, onToggleCollapsed }: SidebarProps) => {
  const { interfaceStore } = useStore();
  const [contentCollapsed, setContentCollapsed] = useState(collapsed);

  useEffect(() => {
    if (!collapsed) {
      setContentCollapsed(false);
      return;
    }

    if (contentCollapsed) {
      return;
    }

    const id = window.setTimeout(() => {
      setContentCollapsed(true);
    }, SIDEBAR_WIDTH_TRANSITION_MS);

    return () => window.clearTimeout(id);
  }, [collapsed, contentCollapsed]);

  return (
    <>
      <SidebarCollapsedProvider value={{ collapsed: contentCollapsed }}>
        <div
          className={clsx(
            'relative hidden shrink-0 transition-[width] duration-300 ease-in-out lg:block',
            collapsed ? 'w-16' : 'w-80'
          )}
          data-testid="sidebar-desktop-shell"
        >
          <aside
            className="flex h-full w-full flex-col overflow-hidden border-r border-gray-200 bg-white"
            data-testid="sidebar-desktop"
            data-collapsed={collapsed ? 'true' : 'false'}
          >
            <div
              className={clsx(
                'flex h-full shrink-0 flex-col',
                contentCollapsed ? 'w-16' : 'w-80'
              )}
              data-testid="sidebar-desktop-inner"
            >
              <div className="hover-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
                <div className="flex min-h-full flex-col">
                  <Menu />
                  <SidebarFooter collapsed={contentCollapsed} />
                </div>
              </div>
            </div>
          </aside>
          {onToggleCollapsed && (
            <IconButton
              mode="ghost"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
              testIdentifier="sidebar-collapse-toggle"
              className={clsx(
                'absolute right-0 z-20 -translate-y-1/2 translate-x-1/2 rounded-full border border-gray-200 !bg-white shadow-sm hover:!bg-white',
                collapsed ? 'top-8' : 'top-[53px]'
              )}
            >
              {collapsed ? (
                <PanelLeftOpenIcon size={18} />
              ) : (
                <PanelLeftCloseIcon size={18} />
              )}
            </IconButton>
          )}
        </div>
      </SidebarCollapsedProvider>

      {/* Mobile menu drawer */}
      <Transition show={interfaceStore.menuDrawerOpen} as={Fragment}>
        <Dialog
          as="div"
          onClose={() => interfaceStore.toggleMenuDrawer(false)}
          className="relative z-50 lg:hidden"
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </Transition.Child>
          <div className="fixed inset-0 w-screen overflow-y-auto">
            <Transition.Child
              as={Fragment}
              enter="transition duration-300 ease-out"
              enterFrom="opacity-0 -translate-x-full"
              enterTo="opacity-100 translate-x-0"
              leave="transition duration-150"
              leaveFrom="opacity-100 translate-x-0"
              leaveTo="opacity-0 -translate-x-full"
            >
              <Dialog.Panel
                className="flex min-h-full max-w-xs flex-col rounded-r-lg bg-white"
                data-testid="sidebar-mobile-panel"
              >
                <div className="flex px-6 py-4">
                  <IconButton
                    variant="tertiary"
                    testIdentifier="sidebar-mobile-close"
                    onClick={() => interfaceStore.toggleMenuDrawer(false)}
                  >
                    <XIcon size={24} />
                  </IconButton>
                </div>
                <SidebarCollapsedProvider value={{ collapsed: false }}>
                  <div className="hover-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto">
                    <div className="flex min-h-full flex-col">
                      <Menu />
                      <SidebarFooter collapsed={false} />
                    </div>
                  </div>
                </SidebarCollapsedProvider>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default Sidebar;
